import { createPortal } from 'react-dom';
import microsoftLogo from '@/common/microsoft-logo.svg';
import type { QueryClient } from '@tanstack/react-query';
import { connectProvider } from '@/features/oauth/lib/connect-provider';
import { archiveModuleWithToast } from '../../_infrastructure/ModuleMenu';
import { IntegrationSyncStatusBadge } from '@/common/components/IntegrationSyncStatusBadge';
import type { CalendarAccount } from '../hooks/useCalendarSync';

/**
 * The module options dropdown (gear / mobile-options popover) for EventListModule
 * (#3277). Renders Rename / Archive / Manage Accounts plus the per-account enable
 * toggles and per-account calendar checkboxes. The orchestrator passes `menuRef`
 * (the same ref the click-outside guard reads) so node identity is preserved.
 */
// Mirrors the `accountCalendars` value shape produced by useCalendarSync:
// `calendars` is always present (possibly empty), `selected` is a boolean, and
// `color` is `string | null` — non-optional so the inline access pattern
// (`calData?.calendars.map`, `cal.selected`) holds.
type AccountCalendarData = {
  loading: boolean;
  error: string | null;
  calendars: Array<{ id: string; name: string; primary: boolean; accessRole: string; color: string | null; selected: boolean }>;
};

export interface CalendarOptionsMenuProps {
  open: boolean;
  position: { top: number; left: number } | null;
  menuRef: React.RefObject<HTMLDivElement | null>;
  setShowOptionsMenu: (v: boolean) => void;
  moduleId: string;
  workspaceId: string | null;
  token: string | null;
  conversationId: string;
  showToast: (message: string, type?: 'success' | 'error' | 'info') => void;
  queryClient: QueryClient;
  loadingAccounts: boolean;
  calendarAccounts: CalendarAccount[];
  expandedAccountIds: Set<string>;
  accountCalendars: Record<string, AccountCalendarData>;
  setShowIntegrationAccountsModal: (v: boolean) => void;
  handleCalendarConnect: (provider: 'google' | 'microsoft') => void;
  handleToggleAccountExpand: (accountId: string) => void;
  handleToggleAccount: (accountId: string, isEnabled: boolean) => void;
  handleToggleCalendar: (accountId: string, calId: string, calName: string, selected: boolean) => void;
}

export function CalendarOptionsMenu(props: CalendarOptionsMenuProps) {
  const {
    open,
    position,
    menuRef,
    setShowOptionsMenu,
    moduleId,
    workspaceId,
    token,
    conversationId,
    showToast,
    queryClient,
    loadingAccounts,
    calendarAccounts,
    expandedAccountIds,
    accountCalendars,
    setShowIntegrationAccountsModal,
    handleCalendarConnect,
    handleToggleAccountExpand,
    handleToggleAccount,
    handleToggleCalendar,
  } = props;

  if (!open || !position) return null;

  return createPortal(
    <>
      <div className="module-options-backdrop" onClick={() => setShowOptionsMenu(false)} />
      <div
        ref={menuRef}
        className="module-options-menu"
        onClick={(e) => e.stopPropagation()}
        style={{
          top: position.top,
          left: position.left,
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0' }}>
          <button
            className="context-menu-item"
            onClick={() => {
              setShowOptionsMenu(false);
              window.dispatchEvent(new CustomEvent('xavior:rename-module', { detail: { moduleId } }));
            }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="context-menu-item-icon">
              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
            </svg>
            Rename
          </button>
          <button
            className="context-menu-item"
            onClick={async () => {
              setShowOptionsMenu(false);
              if (!workspaceId) return;
              await archiveModuleWithToast(token ?? null, workspaceId, moduleId, showToast, queryClient, conversationId);
            }}
            style={{ color: 'var(--color-text-muted)' }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="context-menu-item-icon">
              <polyline points="21 8 21 21 3 21 3 8" />
              <rect x="1" y="3" width="22" height="5" />
              <line x1="10" y1="12" x2="14" y2="12" />
            </svg>
            Archive
          </button>
          {!loadingAccounts && calendarAccounts.length > 0 && (
            <button
              className="context-menu-item"
              onClick={() => {
                setShowIntegrationAccountsModal(true);
                setShowOptionsMenu(false);
              }}
            >
              <img src="/google-logo.svg" width="16" height="16" alt="Google" className="context-menu-item-icon" />
              Manage Accounts
            </button>
          )}
        </div>

        <div style={{ borderTop: '1px solid var(--color-border)' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0' }}>
            {loadingAccounts && (
              <div style={{ padding: '10px 18px', fontSize: '14px', color: 'var(--color-text-faint)' }}>
                Loading accounts...
              </div>
            )}
            {/* Provider-agnostic first-connect (ADR 017 D4 / U2) — mirrors the
                module-body empty state (EventListModule). No Google-only element:
                offer every groupware provider via the provider-generic
                handleCalendarConnect (OAuth popup owns the provider). */}
            {!loadingAccounts && calendarAccounts.length === 0 && (
              <>
                <button
                  className="context-menu-item"
                  onClick={() => {
                    handleCalendarConnect('google');
                    setShowOptionsMenu(false);
                  }}
                >
                  <img src="/google-logo.svg" width="16" height="16" alt="" className="context-menu-item-icon" />
                  Connect Google
                </button>
                <button
                  className="context-menu-item"
                  onClick={() => {
                    handleCalendarConnect('microsoft');
                    setShowOptionsMenu(false);
                  }}
                >
                  <img src={microsoftLogo} width="16" height="16" alt="" className="context-menu-item-icon" />
                  Connect Microsoft
                </button>
              </>
            )}
            {!loadingAccounts && calendarAccounts.length > 0 && (
              <>
                {calendarAccounts.map((account, idx) => {
                  // #1575: when only one Google account is connected, render the
                  // calendar dropdown inline (no expand toggle). The previous UX
                  // hid the calendar selection behind an expand-account step that
                  // single-account users had no reason to discover, leaving them
                  // unable to pick which of that account's calendars to sync.
                  const isSingleAccount = calendarAccounts.length === 1;
                  const isExpanded = isSingleAccount || expandedAccountIds.has(account.id);
                  const calData = accountCalendars[account.id];
                  const isEnabled = account.enabledForModule ?? false;
                  const isDisabled = account.status === 'disabled';
                  return (
                    <div key={account.id} style={{ ...(idx > 0 ? { borderTop: '1px solid var(--color-border)' } : {}), overflow: 'hidden' }}>
                      <div
                        onClick={isSingleAccount || isDisabled ? undefined : () => handleToggleAccountExpand(account.id)}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          padding: '10px 18px',
                          backgroundColor: 'transparent',
                          cursor: isSingleAccount ? 'default' : 'pointer',
                        }}
                      >
                        <div style={{ flex: 1, minWidth: 0, marginRight: '8px', display: 'flex', flexDirection: 'column', gap: 2 }}>
                          <span
                            style={{
                              fontSize: '14px',
                              color: isDisabled ? 'var(--color-text-muted)' : 'var(--text-primary, var(--color-text))',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                            title={account.email}
                          >
                            {account.email}
                          </span>
                          {isDisabled && (
                            <span style={{ fontSize: '11px', color: 'var(--color-text-muted)' }}>
                              Disconnected — data retained
                            </span>
                          )}
                          {/*
                            Sync error hierarchy (deliberately limited):
                              1. THIS per-account badge — scoped to one row, shows
                                 mixed-error states the empty-state banner can't.
                              2. Empty-state banner (rendered below) — only when
                                 EVERY enabled account is in the same error state
                                 AND there are no events to display.
                            We intentionally do NOT render a panel-header banner
                            in addition; that would triple-stack the same message.
                          */}
                          {account.syncErrorCode && (
                            <IntegrationSyncStatusBadge
                              syncErrorCode={account.syncErrorCode}
                              syncError={account.syncError}
                              syncErrorAt={account.syncErrorAt}
                            />
                          )}
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
                          {isDisabled ? (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                if (account.provider === 'microsoft') {
                                  try { sessionStorage.setItem('email_oauth_in_progress', 'true'); } catch { /* best-effort */ }
                                  // No integrationId → FLOW A (enroll new account): picker shows.
                                  void connectProvider({ provider: 'microsoft', authToken: token })
                                    .catch(() => { try { sessionStorage.removeItem('email_oauth_in_progress'); } catch { /* best-effort */ } });
                                } else {
                                  window.dispatchEvent(new Event('triggerGoogleOAuth'));
                                }
                              }}
                              style={{
                                background: 'none', border: '1px solid var(--color-border)', borderRadius: '6px',
                                cursor: 'pointer', padding: '4px 10px', fontSize: '12px', fontWeight: 600,
                                color: 'var(--color-primary, #1a9ba5)', whiteSpace: 'nowrap',
                              }}
                              title="Reconnect this account (resumes syncing; retained data stays)"
                            >
                              Reconnect
                            </button>
                          ) : (
                            <>
                          {!isSingleAccount && (
                            <svg
                              width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                              style={{
                                flexShrink: 0,
                                color: 'var(--color-text-faint)',
                                transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                                transition: 'transform 0.2s ease',
                              }}
                            >
                              <polyline points="9 18 15 12 9 6" />
                            </svg>
                          )}
                          <div
                            onClick={(e) => { e.stopPropagation(); handleToggleAccount(account.id, isEnabled); }}
                            title={isEnabled ? 'Click to disable' : 'Click to enable'}
                            style={{
                              position: 'relative',
                              width: '36px',
                              height: '20px',
                              background: isEnabled ? 'rgba(34, 197, 94, 0.85)' : 'var(--color-surface-hover)',
                              borderRadius: '10px',
                              transition: 'background 0.3s ease',
                              cursor: 'pointer',
                            }}
                          >
                            <div
                              style={{
                                position: 'absolute',
                                top: '2px',
                                left: isEnabled ? '18px' : '2px',
                                width: '16px',
                                height: '16px',
                                background: 'var(--color-surface)',
                                borderRadius: '50%',
                                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.15)',
                                transition: 'left 0.3s ease',
                              }}
                            />
                          </div>
                            </>
                          )}
                        </div>
                      </div>
                      {!isDisabled && isEnabled && isExpanded && (
                        <div style={{
                          padding: '0 18px 8px 18px',
                          backgroundColor: 'var(--color-surface)',
                        }}>
                          {calData?.loading && (
                            <div style={{ fontSize: '11px', color: 'var(--color-text-faint)', padding: '3px 0' }}>Loading calendars...</div>
                          )}
                          {calData?.error && (
                            <div style={{ fontSize: '11px', color: '#ef4444', padding: '3px 0' }}>{calData.error}</div>
                          )}
                          {!calData?.loading && !calData?.error && calData?.calendars.map(cal => {
                            const calColor = cal.color || '#444';
                            return (
                              <div
                                key={cal.id}
                                onClick={() => handleToggleCalendar(account.id, cal.id, cal.name, cal.selected)}
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '7px',
                                  padding: '3px 0',
                                  cursor: 'pointer',
                                  fontSize: '12px',
                                  color: 'var(--color-text-subtle)',
                                }}
                              >
                                <div style={{
                                  width: '14px',
                                  height: '14px',
                                  borderRadius: '2px',
                                  border: `2px solid ${calColor}`,
                                  backgroundColor: cal.selected ? calColor : 'transparent',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  flexShrink: 0,
                                  transition: 'background-color 0.15s ease',
                                }}>
                                  {cal.selected && (
                                    <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                                      <polyline points="20 6 9 17 4 12" />
                                    </svg>
                                  )}
                                </div>
                                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                  {cal.primary ? 'Primary' : cal.name}
                                </span>
                              </div>
                            );
                          })}
                          {!calData?.loading && !calData?.error && (!calData?.calendars || calData.calendars.length === 0) && (
                            <div style={{ fontSize: '11px', color: 'var(--color-text-faint)', padding: '3px 0' }}>No calendars found</div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </>
            )}
          </div>
        </div>
    </div>
    </>,
    document.getElementById('modal-portal-root') || document.body
  );
}
